import { z } from 'zod';

// Exercise Logger Schema - keeping string inputs for form compatibility
export const exerciseLoggerSchema = z.object({
  selectedExercise: z.string()
    .min(1, 'Please select an exercise')
    .trim(),
  weight: z.string()
    .min(1, 'Weight is required')
    .refine((val) => !isNaN(Number(val)) && Number(val) !== 0, {
      message: 'Weight must be a non-zero number (use negative for assisted exercises)'
    }),
  reps: z.string()
    .min(1, 'Reps is required')
    .refine((val) => !isNaN(Number(val)) && Number(val) > 0 && Number(val) <= 1000, {
      message: 'Reps must be between 1 and 1000'
    }),
  sets: z.string()
    .min(1, 'Sets is required')
    .refine((val) => !isNaN(Number(val)) && Number(val) > 0 && Number(val) <= 50, {
      message: 'Sets must be between 1 and 50'
    }),
  notes: z.string().optional()
});

export type ExerciseLoggerFormData = z.infer<typeof exerciseLoggerSchema>;

// Helper function to convert form data to API format
export const transformExerciseFormData = (data: ExerciseLoggerFormData) => ({
  exercise: data.selectedExercise,
  weight: parseFloat(data.weight),
  reps: parseInt(data.reps),
  sets: parseInt(data.sets),
  notes: data.notes || undefined,
});

// Custom Exercise Schema
export const customExerciseSchema = z.object({
  name: z.string()
    .min(3, 'Exercise name must be at least 3 characters')
    .max(50, 'Exercise name must be less than 50 characters')
    .trim()
    .refine((val) => val.length > 0, {
      message: 'Exercise name is required'
    })
});

export type CustomExerciseFormData = z.infer<typeof customExerciseSchema>;

// Auth Form Schema
export const authFormSchema = z.object({
  email: z.string()
    .min(1, 'Email is required')
    .email({ message: 'Please enter a valid email address' })
    .trim()
    .toLowerCase()
});

export type AuthFormData = z.infer<typeof authFormSchema>;

// Photo Upload Schema (for date validation)
export const photoUploadSchema = z.object({
  photoDate: z.date({
    message: 'Please select a valid date'
  })
  .refine((date) => date <= new Date(), {
    message: 'Photo date cannot be in the future'
  })
  .refine((date) => date >= new Date('1900-01-01'), {
    message: 'Photo date must be after 1900'
  })
});

export type PhotoUploadFormData = z.infer<typeof photoUploadSchema>;

// Validation helper functions
export const getFieldError = (fieldName: string, errors: Record<string, any>): string | undefined => {
  const error = errors[fieldName];
  return error?.message;
};

export const hasFieldError = (fieldName: string, errors: Record<string, any>): boolean => {
  return !!errors[fieldName];
};

export const isFormValid = (errors: Record<string, any>): boolean => {
  return Object.keys(errors).length === 0;
};

// Field validation states
export type FieldValidationState = 'idle' | 'valid' | 'error';

export const getFieldValidationState = (
  fieldName: string, 
  errors: Record<string, any>, 
  touched: Record<string, boolean>
): FieldValidationState => {
  if (!touched[fieldName]) return 'idle';
  return hasFieldError(fieldName, errors) ? 'error' : 'valid';
};